'use client';

import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Avatar,
  Box,
  Card,
  CardContent,
  IconButton,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from '@mui/material';
import { PersonOutline, InfoOutlined, ExpandMore, Check, Close } from '@mui/icons-material';
import { useState } from 'react';
import l from '../../../../../../lang';

interface Permission {
  text: string;
}

interface RoleCardProps {
  id?: string;
  title: string;
  subtitle: string;
  description: string;
  canDo?: Permission[];
  cannotDo?: Permission[];
  icon?: React.ReactNode;
  selected?: boolean;
  onSelect?: (id: string) => void;
  disabled?: boolean;
  tooltip?: string;
}

export default function RoleCard({
  id,
  title,
  subtitle,
  description,
  icon = <PersonOutline sx={{ fontSize: 36 }} />,
  canDo = [],
  cannotDo = [],
  selected = false,
  onSelect = () => {},
  disabled = false,
  tooltip = '',
}: RoleCardProps) {
  const [expandedCanDo, setExpandedCanDo] = useState(false);
  const [expandedCannotDo, setExpandedCannotDo] = useState(false);

  return (
    <Card
      sx={{
        width: '100%',
        borderRadius: 3,
        boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
        border: selected ? '2px solid #4267f5' : '1px solid #e0e0e0',
        margin: '0 auto',
        cursor: disabled ? 'not-allowed' : 'pointer',
      }}
      onClick={() => !disabled && id && onSelect(id)}
    >
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
          <Avatar
            sx={{
              width: 72,
              height: 72,
              bgcolor: '#e3eafc',
              color: '#4267f5',
              mr: 2,
            }}
          >
            {icon}
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" component="h2" sx={{ fontWeight: 600, color: '#1a1a1a', mb: 0.5 }}>
              {title}
            </Typography>
            <Typography variant="body2" sx={{ color: '#666' }}>
              {subtitle}
            </Typography>
          </Box>
          <IconButton
            size="small"
            sx={{
              color: '#4267f5',
              border: '1px solid #4267f5',
              width: 28,
              height: 28,
            }}
            title={tooltip}
          >
            <Tooltip title={tooltip}>
              <InfoOutlined sx={{ fontSize: 16, cursor: 'pointer' }} />
            </Tooltip>
          </IconButton>
        </Box>

        <Typography variant="body1" sx={{ color: '#333', mb: 3 }}>
          {description}
        </Typography>

        <Accordion
          expanded={expandedCanDo}
          onChange={() => setExpandedCanDo(!expandedCanDo)}
          disableGutters
          elevation={0}
          sx={{
            border: '1px solid #e0e0e0',
            borderRadius: '12px !important',
            mb: 2,
            '&:before': { display: 'none' },
            overflow: 'hidden',
          }}
        >
          <AccordionSummary
            expandIcon={
              <ExpandMore
                sx={{
                  transform: expandedCanDo ? 'rotate(0deg)' : 'rotate(180deg)',
                  transition: 'transform 0.2s',
                }}
              />
            }
            sx={{
              px: 2.5,
              py: 1.5,
              '& .MuiAccordionSummary-content': { my: 0 },
            }}
          >
            <Typography sx={{ fontWeight: 600, color: '#1a1a1a' }}>{l('userManagement.step2.card_role.canDo')}</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 2.5, pt: 0, pb: 2 }}>
            <List dense disablePadding>
              {canDo.map((item, index) => (
                <ListItem key={index} disablePadding sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <Check sx={{ color: '#22c55e', fontSize: 20 }} />
                  </ListItemIcon>
                  <ListItemText primary={item.text} primaryTypographyProps={{ variant: 'body2', color: '#444' }} />
                </ListItem>
              ))}
            </List>
          </AccordionDetails>
        </Accordion>

        <Accordion
          expanded={expandedCannotDo}
          onChange={() => setExpandedCannotDo(!expandedCannotDo)}
          disableGutters
          elevation={0}
          sx={{
            border: '1px solid #e0e0e0',
            borderRadius: '12px !important',
            '&:before': { display: 'none' },
            overflow: 'hidden',
          }}
        >
          <AccordionSummary
            expandIcon={
              <ExpandMore
                sx={{
                  transform: expandedCannotDo ? 'rotate(0deg)' : 'rotate(180deg)',
                  transition: 'transform 0.2s',
                }}
              />
            }
            sx={{
              px: 2.5,
              py: 1.5,
              '& .MuiAccordionSummary-content': { my: 0 },
            }}
          >
            <Typography sx={{ fontWeight: 600, color: '#1a1a1a' }}>
              {l('userManagement.step2.card_role.cannotDo')}
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 2.5, pt: 0, pb: 2 }}>
            <List dense disablePadding>
              {cannotDo.map((item, index) => (
                <ListItem key={index} disablePadding sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <Close sx={{ color: '#ef4444', fontSize: 20 }} />
                  </ListItemIcon>
                  <ListItemText primary={item.text} primaryTypographyProps={{ variant: 'body2', color: '#444' }} />
                </ListItem>
              ))}
            </List>
          </AccordionDetails>
        </Accordion>
      </CardContent>
    </Card>
  );
}
